import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MetricCard } from "@/components/projects/diagnostic/primitives/MetricCard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Activity,
  AlertCircle,
  Boxes,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Download,
  ExternalLink,
  FileText,
  FileWarning,
  Inbox,
  Info,
  Layers,
  Loader2,
  RefreshCw,
  Scale,
  ScanLine,
  Search,
  ShieldAlert,
  Sparkles,
  Wrench,
} from "lucide-react";

import { AdjustedBadge, WricefDecisionSection } from "@/components/agents/WricefDecisionSection";
import { DECISION_BAR } from "@/components/projects/diagnostic/acts/environment/WricefCard";
import {
  DECISION_ORDER,
  type DecisionOrUndecided,
  UNDECIDED,
} from "@/components/projects/diagnostic/data/wricefDecisions";
import {
  makeDecisionMeta,
  makeExtensionTargetMeta,
} from "@/components/projects/diagnostic/diagnosticMeta";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { WricefDecisionTotals } from "@/components/agents/WricefDecisionTotals";
import { WricefBulkDecisionBar } from "@/components/agents/WricefBulkDecisionBar";
import { WricefClientReportButton } from "@/components/agents/WricefClientReportButton";
import { DecisionCheckbox } from "@/components/agents/configuration-server/stages/DecisionRows";
import { useCurrentUser } from "@/hooks/useAuth";
import { useResettingSelection } from "@/hooks/useResettingSelection";
import { useWricefDecision, useWricefDecisionSummary } from "@/hooks/useWricefDecisions";
import { WricefUsageRuleButton } from "./WricefUsageRuleButton";
import { formatCount } from "@/lib/format";
import { hasMinRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

import {
  useWricefByPackage,
  useWricefCatalog,
  useWricefCatalogSummary,
  useWricefComponents,
} from "@/hooks/useWricefCatalog";
import { useFullbaseStatus } from "@/hooks/useFullbase";
import { useResettingPage } from "@/hooks/useResettingPage";
import { wricefCoverage } from "@/lib/wricef-coverage";
import { tp } from "@/lib/plural";
import { listViewQuery } from "@/lib/wricef-list-view";
import {
  downloadWricefRepository,
  downloadWricefSource,
  fetchAllWricefCatalog,
  openWricefSourceInTab,
} from "@/api/wricef-catalog";
import {
  CONVERSION_COLORS,
  NOT_APPLICABLE_BUCKET,
  UNKNOWN_STREAM,
  type WricefBucket,
  WRICEF_COLORS,
  WRICEF_NAMES,
  type WricefCatalogItem,
  type WricefCatalogQuery,
  type WricefComponent,
  type WricefLetter,
  type WricefPackageSummary,
} from "@/types/wricef";
import {
  CLEAN_CORE_BADGE_COLOR,
  CONFIDENCE_BADGE_COLOR,
  CRITICALITY_BADGE_COLOR,
  CRITICALITY_BAR_COLOR,
  CRITICALITY_FACTOR_EXPLANATIONS,
  REVIEW_STATUS_COLORS,
  cleanCoreLevelMeta,
  formatEffort,
  getConfidenceLabel,
  getConversionLabel,
  getCriticalityLabel,
  getReviewStatus,
  getSourceLabel,
  hasRichAnalysis,
  hoursLabel,
  streamLabel,
} from "@/lib/wricef-status";
import {
  formatDateOnly,
  formatDateTime,
  yearOfDateOnly,
  yearsSince,
} from "@/lib/datetime";
import { buildCatalogCsv } from "@/components/agents/wricefCatalogCsv";
import { CatalogPager } from "@/components/agents/cleancore/CatalogPager";

interface WricefCatalogTabProps {
  projectId: string;
  /** Quando setado, filtra o catálogo ao snapshot daquela varredura. */
  scanJobId?: string | null;
}

const WRICEF_LETTERS: WricefLetter[] = ["W", "R", "I", "C", "E", "F"];

/** A seleção da decisão em lote (#1501) que a grade desenha. Ausente para quem não decide. */
interface CatalogSelection {
  checked: ReadonlySet<string>;
  onToggle: (objNames: string[], checked: boolean) => void;
}

/** Linhas por página. A lista inteira tem milhares: 7.288 no IDES full-base. */
const PAGE_SIZE = 50;

/**
 * Os módulos do eixo resolvido, do maior para o menor, com o balde sem módulo no
 * fim por maior que seja. Mesma regra do card do Diagnóstico: com metade do
 * catálogo sem módulo, deixá-lo liderar faria a lista abrir por "não sabemos".
 */
/** O nome da categoria WRICEF no idioma de quem lê. Objeto sem letra (classe,
 *  grupo de função) não tem nome, e devolve vazio em vez de "undefined". */
function wricefNameOf(t: ReturnType<typeof useTranslation>["t"], letter: string | null): string {
  const name = letter ? WRICEF_NAMES[letter as WricefLetter] : undefined;
  return name ? t(name) : "";
}

function streamsBySize(byStream?: Record<string, number>): Array<[string, number]> {
  return Object.entries(byStream ?? {}).sort((a, b) => {
    if (a[0] === UNKNOWN_STREAM) return 1;
    if (b[0] === UNKNOWN_STREAM) return -1;
    return b[1] - a[1] || a[0].localeCompare(b[0]);
  });
}

// Header de coluna com legenda no tooltip — os códigos (WRICEF W/R/I/C/E/F,
// Clean Core A/B/C/D) não são óbvios pra quem abre o catálogo.
function ColumnHint({
  label,
  legend,
}: {
  label: string;
  legend: Array<{ code: string; name: string }>;
}) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 cursor-help border-b border-dotted border-muted-foreground/40">
            {label}
            <Info className="h-3 w-3 opacity-50" aria-hidden="true" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[280px]">
          <ul className="space-y-1 text-xs leading-snug">
            {legend.map((l) => (
              <li key={l.code}>
                <span className="font-semibold">{l.code}</span> — {l.name}
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ===========================================================================
// Página principal
// ===========================================================================

export function WricefCatalogTab({ projectId, scanJobId }: WricefCatalogTabProps) {
  // ----- Estado de filtros -----
  const [selectedLetters, setSelectedLetters] = useState<Set<WricefLetter>>(
    new Set(),
  );
  const [selectedStreams, setSelectedStreams] = useState<Set<string>>(new Set());
  const [criticality, setCriticality] = useState<[number, number]>([0, 1]);
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  // A listagem abre nos entregáveis, não em tudo. Medido na execução contra o
  // produtivo do primeiro cliente: das 141 linhas, 93 são grupo de função e
  // classe, e sobram 48 programas. A lista abria 66% composta de peça, e foi o
  // ponto ao qual o consultor voltou quatro vezes na reunião de 06/10 (#1083).
  const [objectState, setObjectState] = useState<string>("entregavel");
  // A decisão do arquiteto (#1500). O painel liga e desliga várias; o filtro ao
  // lado do CSV escolhe uma só. Os dois mexem neste mesmo estado.
  const [selectedDecisions, setSelectedDecisions] = useState<Set<DecisionOrUndecided>>(
    new Set(),
  );
  // EF-FUNC-011: o "Fora do escopo" sem motivo, para dar o motivo em lote sem
  // trocar o de quem já tinha.
  const [withoutReason, setWithoutReason] = useState(false);
  const [orderBy, setOrderBy] = useState<WricefCatalogQuery["order_by"]>(
    "criticality_score",
  );
  const [orderDesc, setOrderDesc] = useState(true);
  const [selected, setSelected] = useState<WricefCatalogItem | null>(null);
  const [view, setView] = useState<"list" | "package">("list");
  const [exporting, setExporting] = useState(false);

  const hasActiveFilters = useMemo(
    () =>
      selectedLetters.size > 0 ||
      selectedStreams.size > 0 ||
      criticality[0] > 0 ||
      criticality[1] < 1 ||
      sourceFilter !== "all" ||
      objectState !== "entregavel" ||
      selectedDecisions.size > 0 ||
      withoutReason,
    [
      selectedLetters,
      selectedStreams,
      criticality,
      sourceFilter,
      objectState,
      selectedDecisions,
      withoutReason,
    ],
  );

  // O filtro sem a página: é ele que o CSV exporta inteiro e é a chave que
  // devolve a paginação para a página 1 quando muda.
  const filterQuery: WricefCatalogQuery = useMemo(() => {
    const q: WricefCatalogQuery = {
      order_by: orderBy,
      order_desc: orderDesc,
    };
    if (selectedLetters.size > 0) q.wricef = Array.from(selectedLetters);
    if (selectedStreams.size > 0) q.stream = Array.from(selectedStreams);
    if (criticality[0] > 0) q.min_criticality = criticality[0];
    if (criticality[1] < 1) q.max_criticality = criticality[1];
    if (sourceFilter !== "all")
      q.source = sourceFilter as "zip" | "tadir" | "both";
    // "all" é ausência de filtro: a api devolve tudo, como fazia antes.
    Object.assign(q, listViewQuery(objectState));
    // Na ordem do card, e não na do clique: a mesma escolha é a mesma consulta,
    // e o react-query não lê de novo à toa.
    if (selectedDecisions.size > 0) {
      q.decision = DECISION_ORDER.filter((token) => selectedDecisions.has(token));
    }
    if (withoutReason) q.out_of_scope_without_reason = true;
    if (scanJobId) q.scan_job_id = scanJobId;
    return q;
  }, [
    selectedLetters,
    selectedStreams,
    criticality,
    sourceFilter,
    objectState,
    selectedDecisions,
    withoutReason,
    orderBy,
    orderDesc,
    scanJobId,
  ]);

  const [page, setPage] = useResettingPage(JSON.stringify(filterQuery));
  const query: WricefCatalogQuery = useMemo(
    () => ({ ...filterQuery, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
    [filterQuery, page],
  );

  const summaryQuery = useWricefCatalogSummary(projectId, scanJobId);
  // A cobertura lê o resumo do PROJETO, não o do scan selecionado.
  //
  // O `sourceTotal` vem do `useFullbaseStatus`, que é por projeto e ignora o
  // scan por construção. O resumo com `scanJobId` reflete só aquele snapshot
  // (`wricef_catalog.py:252`), então com um scan escolhido no histórico o
  // numerador passava a ser de um job e o denominador continuava do projeto —
  // 46% viravam 1%, e o `baseFullyCatalogued` virava falso por comparação entre
  // escopos diferentes.
  //
  // Com `scanJobId` nulo as duas chamadas têm a mesma chave e o react-query as
  // deduplica, então o caso comum não paga nada.
  const coverageSummaryQuery = useWricefCatalogSummary(projectId);
  const listQuery = useWricefCatalog(projectId, query, { keepPreviousPage: true });
  const fullbaseQuery = useFullbaseStatus(projectId);
  // Os totais da decisão do topo (#1500) são do projeto, como o card do Ato 1:
  // não levam o scan nem os filtros da grade.
  const decisionSummaryQuery = useWricefDecisionSummary(projectId);

  // Cobertura: o catálogo é cumulativo (recorte + varredura caem na mesma
  // tabela). Isto diz se o que está na tela é a base toda ou um pedaço, e
  // alimenta a ponte recorte → varredura completa.
  const coverage = useMemo(
    () =>
      wricefCoverage({
        catalogTotal: coverageSummaryQuery.data?.total ?? 0,
        extractedPrograms: fullbaseQuery.data?.extracted_programs ?? 0,
        // #829: catalogado não é analisado. O `_UNKNOWN` do
        // `by_clean_core_level` são as linhas que entraram na tabela e não
        // passaram pela análise, e sem elas a tela afirmava cobertura completa
        // sobre uma base 41% não classificada.
        unclassified:
            coverageSummaryQuery.data?.by_clean_core_level?.["_UNKNOWN"] ?? 0,
        // #829: o denominador é o universo de ORIGEM, e não o escopo do último
        // job. Sem ele, 148 analisados de 252 contra os 91 do último job davam
        // "covered" — e um job de pacote pequeno fechava a cobertura sozinho.
        sourceTotal: fullbaseQuery.data?.source_total,
      }),
    [coverageSummaryQuery.data, fullbaseQuery.data],
  );
  const fullbaseRunning = Boolean(fullbaseQuery.data?.running);

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;

  // A decisão em lote (#1501) é do consultor, como a decisão de um objeto.
  const canDecideInBulk = hasMinRole(useCurrentUser()?.role, "consultant");
  // A seleção se apaga quando o filtro muda, e não com a ordem ou a página:
  // ordenar não muda quem está no filtro.
  const selectionKey = useMemo(
    () => JSON.stringify({ ...filterQuery, order_by: undefined, order_desc: undefined }),
    [filterQuery],
  );
  const [bulkSelection, setBulkSelection] = useResettingSelection(selectionKey);
  const [selectingAll, setSelectingAll] = useState(false);
  // Só objeto com letra leva decisão; o lote recusaria os outros.
  const pageSelectable = items.filter((row) => row.wricef).map((row) => row.obj_name);
  // Quantos objetos com letra o filtro tem. A contagem por decisão já segue os
  // outros filtros e só conta objeto com letra (#1500); com decisão escolhida,
  // soma só as escolhidas.
  const decisionCounts = listQuery.data?.decision_counts;
  const selectableTotal = useMemo(() => {
    if (!decisionCounts) return 0;
    const tokens =
      selectedDecisions.size > 0 ? Array.from(selectedDecisions) : Object.keys(decisionCounts);
    return tokens.reduce((sum, token) => sum + (decisionCounts[token] ?? 0), 0);
  }, [decisionCounts, selectedDecisions]);

  const toggleLetter = useCallback((letter: WricefLetter) => {
    setSelectedLetters((prev) => {
      const next = new Set(prev);
      if (next.has(letter)) next.delete(letter);
      else next.add(letter);
      return next;
    });
  }, []);

  const toggleStream = useCallback((stream: string) => {
    setSelectedStreams((prev) => {
      const next = new Set(prev);
      if (next.has(stream)) next.delete(stream);
      else next.add(stream);
      return next;
    });
  }, []);

  /** O painel: liga ou desliga uma decisão, como as letras. */
  const toggleDecision = useCallback((token: DecisionOrUndecided) => {
    setSelectedDecisions((prev) => {
      const next = new Set(prev);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return next;
    });
  }, []);

  /** O filtro ao lado do CSV: uma decisão só, ou nenhuma ("All"), como no drill-down. */
  const chooseDecision = useCallback((token: DecisionOrUndecided | null) => {
    setSelectedDecisions(token ? new Set([token]) : new Set());
  }, []);

  const resetFilters = useCallback(() => {
    setSelectedLetters(new Set());
    setSelectedStreams(new Set());
    setObjectState("entregavel");
    setCriticality([0, 1]);
    setSourceFilter("all");
    setSelectedDecisions(new Set());
    setWithoutReason(false);
  }, []);

  // O CSV é o filtro inteiro, página a página, e não a página da tela: com 50
  // linhas por página, exportar o que está visível entregaria 50 de 7.288.
  const exportCsv = useCallback(async () => {
    if (!total) return;
    setExporting(true);
    try {
      const rows = await fetchAllWricefCatalog(projectId, filterQuery);
      downloadCsv(rows, `wricef-catalog-${projectId}.csv`);
    } catch (e) {
      console.error("exportar o CSV do catálogo WRICEF falhou", e);
    } finally {
      setExporting(false);
    }
  }, [total, projectId, filterQuery]);

  const toggleBulk = useCallback(
    (objNames: string[], checked: boolean) => {
      const next = new Set(bulkSelection);
      for (const name of objNames) {
        if (checked) next.add(name);
        else next.delete(name);
      }
      setBulkSelection(next);
    },
    [bulkSelection, setBulkSelection],
  );

  // Todos do filtro, e não só da página, pela mesma busca do CSV.
  const selectAllOfFilter = useCallback(async () => {
    setSelectingAll(true);
    try {
      const rows = await fetchAllWricefCatalog(projectId, filterQuery);
      setBulkSelection(new Set(rows.filter((row) => row.wricef).map((row) => row.obj_name)));
    } catch (e) {
      console.error("marcar todos do filtro do catálogo WRICEF falhou", e);
    } finally {
      setSelectingAll(false);
    }
  }, [projectId, filterQuery, setBulkSelection]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6">
        <CoverageBanner
          coverage={coverage}
          running={fullbaseRunning}
          loading={summaryQuery.isLoading || fullbaseQuery.isLoading}
        />

        <DemoContentBanner count={summaryQuery.data?.demo_content ?? 0} />

        <SummaryHeader
          loading={summaryQuery.isLoading}
          summary={summaryQuery.data}
        />

        <WricefDecisionTotals summary={decisionSummaryQuery.data} />

        <ViewToggle view={view} onChange={setView} />

        {view === "package" ? (
          <PackageGroupsView projectId={projectId} scanJobId={scanJobId} />
        ) : (
          <>
            <FiltersPanel
              selectedLetters={selectedLetters}
              onToggleLetter={toggleLetter}
              selectedStreams={selectedStreams}
              onToggleStream={toggleStream}
              criticality={criticality}
              onCriticalityChange={setCriticality}
              sourceFilter={sourceFilter}
              onSourceFilterChange={setSourceFilter}
              objectState={objectState}
              onObjectStateChange={setObjectState}
              selectedDecisions={selectedDecisions}
              onToggleDecision={toggleDecision}
              countsByDecision={listQuery.data?.decision_counts}
              countsByWricef={summaryQuery.data?.by_wricef}
              countsByStream={summaryQuery.data?.by_stream}
              streamLabels={summaryQuery.data?.stream_labels}
              hasActiveFilters={hasActiveFilters}
              onReset={resetFilters}
            />

            <CatalogContent
              loading={listQuery.isLoading}
              error={listQuery.error as Error | null}
              onRetry={() => listQuery.refetch()}
              items={items}
              total={total}
              totalInProject={summaryQuery.data?.total_rows ?? total}
              hasActiveFilters={hasActiveFilters}
              orderBy={orderBy}
              onOrderByChange={setOrderBy}
              orderDesc={orderDesc}
              onOrderDescToggle={() => setOrderDesc((d) => !d)}
              onExport={() => void exportCsv()}
              exporting={exporting}
              projectId={projectId}
              reportQuery={filterQuery}
              decisionCounts={listQuery.data?.decision_counts}
              selectedDecisions={selectedDecisions}
              onChooseDecision={chooseDecision}
              withoutReason={{
                count: listQuery.data?.out_of_scope_without_reason ?? 0,
                active: withoutReason,
                onToggle: () => setWithoutReason((on) => !on),
              }}
              onSelect={setSelected}
              page={page}
              onPageChange={setPage}
              selection={
                canDecideInBulk ? { checked: bulkSelection, onToggle: toggleBulk } : undefined
              }
              selectionBar={
                canDecideInBulk ? (
                  <WricefBulkDecisionBar
                    projectId={projectId}
                    selected={bulkSelection}
                    filterTotal={selectableTotal}
                    pageSelectable={pageSelectable.length}
                    selectingAll={selectingAll}
                    onSelectAll={() => void selectAllOfFilter()}
                    onClear={() => setBulkSelection(new Set())}
                    // A grade, os totais e o card releem pelo hook do lote; a
                    // seleção já cumpriu o papel.
                    onApplied={() => setBulkSelection(new Set())}
                  />
                ) : undefined
              }
            />
          </>
        )}

        <Dialog
          open={Boolean(selected)}
          onOpenChange={(o) => !o && setSelected(null)}
        >
          <DialogContent className="max-w-5xl w-[95vw] p-0 gap-0 h-[85vh] flex flex-col overflow-hidden">
            {selected && <ObjectDetailModal projectId={projectId} item={selected} />}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

function downloadCsv(rows: WricefCatalogItem[], filename: string) {
  const blob = new Blob([buildCatalogCsv(rows)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ===========================================================================
// Toggle Lista | Por pacote
// ===========================================================================

function ViewToggle({
  view,
  onChange,
}: {
  view: "list" | "package";
  onChange: (v: "list" | "package") => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="inline-flex rounded-md border p-0.5 text-xs">
      <button
        onClick={() => onChange("list")}
        className={`rounded px-3 py-1.5 transition-colors ${
          view === "list"
            ? "bg-background font-medium shadow-sm"
            : "text-muted-foreground hover:bg-background/60"
        }`}
      >
        {t("List")}
      </button>
      <button
        onClick={() => onChange("package")}
        className={`rounded px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
          view === "package"
            ? "bg-background font-medium shadow-sm"
            : "text-muted-foreground hover:bg-background/60"
        }`}
      >
        <Layers className="h-3.5 w-3.5" />
        {t("By package")}
      </button>
    </div>
  );
}

// ===========================================================================
// Visão por pacote (devclass) — agrupamento natural do time ABAP
// ===========================================================================

function PackageGroupsView({
  projectId,
  scanJobId,
}: {
  projectId: string;
  scanJobId?: string | null;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useWricefByPackage(
    projectId,
    scanJobId,
  );
  const [open, setOpen] = useState<Set<string>>(new Set());

  const toggle = (dc: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(dc)) next.delete(dc);
      else next.add(dc);
      return next;
    });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">
            {t("Failed to load the package summary.")}
          </span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            {t("Retry")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const packages = data?.packages ?? [];
  if (!packages.length) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          {t("No packages in the catalog yet. Run the base scan.")}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {tp(
          t,
          data?.total_packages ?? 0,
          "{{n}} package · sorted by number of objects",
          "{{n}} packages · sorted by number of objects",
        )}
      </p>
      {packages.map((p) => (
        <PackageCard
          key={p.devclass}
          pkg={p}
          open={open.has(p.devclass)}
          onToggle={() => toggle(p.devclass)}
        />
      ))}
    </div>
  );
}

function PackageCard({
  pkg,
  open,
  onToggle,
}: {
  pkg: WricefPackageSummary;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const crit = getCriticalityLabel(pkg.avg_criticality);
  const effort = formatEffort(pkg.total_effort_h);
  const letters = (Object.keys(pkg.by_wricef) as WricefLetter[]).sort();

  return (
    <div className="rounded-lg border">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
      >
        <span className="font-mono text-sm font-medium flex-1">
          {pkg.devclass}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {t("{{done}}/{{total}} analyzed", { done: pkg.analyzed, total: pkg.total })}
        </span>
        <div className="hidden sm:flex items-center gap-1">
          {letters.map((l) => (
            <Badge
              key={l}
              variant="outline"
              className={`text-[10px] ${WRICEF_COLORS[l]}`}
            >
              {l} {pkg.by_wricef[l]}
            </Badge>
          ))}
        </div>
        <Badge
          variant="outline"
          className={`text-[11px] ${CRITICALITY_BADGE_COLOR[crit.tone]}`}
        >
          {crit.label}
        </Badge>
        {effort && (
          <span className="text-xs text-muted-foreground tabular-nums w-28 text-right hidden md:inline">
            {effort}
          </span>
        )}
      </button>

      {open && (
        <div className="border-t px-4 py-3 space-y-2 text-xs bg-muted/10">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
            <span className="text-muted-foreground">
              {t("Objects:")}{" "}
              <span className="font-medium text-foreground">{pkg.total}</span>
            </span>
            <span className="text-muted-foreground">
              {t("With diagnosis:")}{" "}
              <span className="font-medium text-foreground">{pkg.analyzed}</span>
            </span>
            <span className="text-muted-foreground">
              {t("Average criticality:")}{" "}
              <span className="font-medium text-foreground">
                {pkg.avg_criticality != null
                  ? `${(pkg.avg_criticality * 100).toFixed(0)}/100`
                  : "—"}
              </span>
            </span>
            <span className="text-muted-foreground">
              {t("Total effort:")}{" "}
              <span className="font-medium text-foreground">
                {effort ?? "—"}
              </span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {letters.length ? (
              letters.map((l) => (
                <Badge
                  key={l}
                  variant="outline"
                  className={`text-[11px] ${WRICEF_COLORS[l]}`}
                >
                  {l} · {wricefNameOf(t, l)}: {pkg.by_wricef[l]}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground italic">
                {t("No WRICEF classification yet.")}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Summary header — leitura executiva
// ===========================================================================

// ===========================================================================
// Banner de cobertura — comunica recorte vs base inteira + ponte pra varredura
// ===========================================================================

function CoverageBanner({
  coverage,
  running,
  loading,
}: {
  coverage: ReturnType<typeof wricefCoverage>;
  running: boolean;
  loading: boolean;
}) {
  const { t } = useTranslation();
  if (loading) return null;

  // A ação (rodar/escolher pacotes) vive no painel "Varredura da base" logo
  // acima — sempre visível. Este banner só comunica o ESTADO da cobertura.

  if (running) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-[#ffe2a6] bg-[#fff9ec] px-4 py-3 text-sm">
        <ScanLine className="h-4 w-4 text-[#b57a00] shrink-0 animate-pulse" />
        <span className="text-[#7a5a00]">
          {t("Base scan in progress — the catalog grows as objects are analyzed.")}
        </span>
      </div>
    );
  }

  // Empty: onboarding guiado (D) — o que fazer ao entrar na tela.
  if (coverage.mode === "empty") {
    return (
      <div className="rounded-lg border border-[#dbe3ee] bg-[#f7f9fc] px-4 py-3.5 space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#08254c]">
          <Sparkles className="h-4 w-4 text-[#2f6fed]" />
          {t("Getting started")}
        </div>
        <ol className="text-xs text-[#5b6b82] space-y-1 list-decimal ml-4 leading-relaxed">
          <li>
            {t("In the")} <strong>{t("Base scan")}</strong>{" "}
            {t("panel (above), choose the connection and packages — or leave empty for the entire base.")}
          </li>
          <li>
            {t("Click")} <strong>{t("Estimate")}</strong>{" "}
            {t("and then confirm to run, tracking progress by batch.")}
          </li>
          <li>{t("Review the cataloged objects here — criticality, WRICEF and conversion.")}</li>
        </ol>
      </div>
    );
  }

  // Coberta: confirmação discreta (B).
  if (coverage.mode === "covered") {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-[#cdebd8] bg-[#f1faf4] px-4 py-2.5 text-sm">
        <CheckCircle2 className="h-4 w-4 text-[#27a155] shrink-0" />
        <span className="text-[#1f6b3f]">
          {t("Everything extracted has been analyzed —")}{" "}
          <strong>{coverage.analyzed}</strong>{" "}
          {t("objects. Use the panel above to scan other packages and expand coverage.")}
        </span>
      </div>
    );
  }

  // Slice / Partial: comunica que é parcial (a ação está no painel acima).
  const message =
    coverage.mode === "slice" ? (
      <>
        {t("You are viewing a")} <strong>{t("slice")}</strong>{" "}
        {t("of {{n}} objects. Use the panel above to scan the base or the packages you care about.", { n: coverage.analyzed })}
      </>
    ) : coverage.baseFullyCatalogued ? (
      // #829: a base já foi varrida inteira; o que falta é classificação.
      // Mandar varrer mais aqui seria mandar fazer o que não resolve.
      <>
        {t("The whole extracted base is catalogued, but")}{" "}
        <strong>{coverage.catalogued - coverage.analyzed}</strong>{" "}
        {t("of {{n}} objects have no clean core classification yet —", { n: coverage.catalogued })}{" "}
        <strong>{coverage.analyzed}</strong>{" "}
        {t("analysed ({{pct}}%).", { pct: coverage.pct })}{" "}
        {t("Re-process the catalogued objects to close the gap.")}
      </>
    ) : (
      <>
        {t("Partial coverage:")} <strong>{coverage.analyzed}</strong>{" "}
        {t("of {{extracted}} extracted objects analyzed ({{pct}}%). There is more base to scan in the panel above.", { extracted: coverage.extracted, pct: coverage.pct })}
        {coverage.unclassified > 0 && (
          <>
            {" "}
            {t("Of the")} <strong>{coverage.catalogued}</strong>{" "}
            {t("already catalogued,")}{" "}
            <strong>{coverage.unclassified}</strong>{" "}
            {t("have no clean core classification — re-process them to close that gap.")}
          </>
        )}
      </>
    );

  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-[#ffe2a6] bg-[#fff9ec] px-4 py-3 text-sm">
      <Layers className="h-4 w-4 text-[#b57a00] shrink-0" />
      <span className="text-[#7a5a00]">{message}</span>
    </div>
  );
}

export function SummaryHeader({
  loading,
  summary,
}: {
  loading: boolean;
  summary: ReturnType<typeof useWricefCatalogSummary>["data"];
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full" />
        ))}
      </div>
    );
  }

  if (!summary || summary.total === 0) {
    return null; // empty state handled by CatalogContent
  }

  // O eixo resolvido, e não `by_module`: na passada leve este é `CROSS` em tudo.
  // A chave é o código do componente de aplicação; o texto vem no mapa ao lado.
  const topModules = streamsBySize(summary.by_stream)
    .filter(([m]) => m !== UNKNOWN_STREAM)
    .slice(0, 4)
    .map(([code, count]) => [streamLabel(code, summary.stream_labels), count] as const);
  // Do catálogo, e não da página: os dois eram contados no navegador sobre as
  // linhas carregadas.
  const highCriticality = summary.high_criticality ?? 0;
  const needsReview = summary.needs_review ?? 0;

  // `_NOT_APPLICABLE` sai dos badges: não é uma classificação que falhou, são
  // os 67 tipos que o Guia RICEFW marca N/A porque o RICEFW classifica
  // **entregáveis**, e classe, grupo de funções e interface são parte de um,
  // não um. Medido no appliance: 2.849 de 7.297, o segundo maior balde do
  // card, disputando espaço num eixo cujo assunto é a letra.
  //
  // `_UNKNOWN` fica, e a diferença é o ponto: ele é "nenhuma regra decidiu",
  // que é dívida, enquanto o N/A é resposta.
  const notApplicable = (summary.by_wricef || {})[NOT_APPLICABLE_BUCKET] ?? 0;
  const totalByWricef = Object.entries(summary.by_wricef || {})
    .filter(([letter]) => letter !== NOT_APPLICABLE_BUCKET)
    .sort((a, b) => b[1] - a[1]);

  // O topo conta executável, e não objeto TADIR (#1083). Na base do primeiro
  // cliente são 141 objetos e 48 programas, e o número grande dizia 141 onde o
  // consultor esperava os executáveis. O TADIR continua no card, com o nome
  // dele. Catálogo anterior à agregação da #1073 não tem papel, e mantém o
  // card antigo em vez de afirmar zero executáveis.
  const byRole = summary.by_unit_role ?? {};
  const aggregated = Object.keys(byRole).length > 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {aggregated ? (
        <MetricCard
          icon={<Inbox className="h-4 w-4" />}
          title={t("Executables")}
          value={(byRole.ancora ?? 0).toString()}
          hint={t(
            "What runs: programs, forms, enhancements and services. {{n}} TADIR objects in the catalog, counting the function groups and classes inside them.",
            { n: summary.total_rows ?? summary.total },
          )}
          accent="default"
        />
      ) : (
        <MetricCard
          icon={<Inbox className="h-4 w-4" />}
          title={t("Total cataloged")}
          value={summary.total.toString()}
          hint={t("Custom Z/Y objects identified in the project sources.")}
          accent="default"
        />
      )}
      <MetricCard
        icon={<ShieldAlert className="h-4 w-4" />}
        title={t("High criticality")}
        value={highCriticality.toString()}
        hint={t("Highest potential impact on the Clean Core journey — prioritize review.")}
        accent={highCriticality > 0 ? "danger" : "default"}
      />
      <MetricCard
        icon={<FileWarning className="h-4 w-4" />}
        title={t("Manual review")}
        value={needsReview.toString()}
        hint={t("WRICEF letter defaulted because the TADIR type has no mapping, or the AI could not write the description. A missing description alone is not counted: the light pass never writes one.")}
        accent={needsReview > 0 ? "warn" : "default"}
      />
      <Card className="shadow-none border">
        <CardContent className="pt-4 space-y-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Layers className="h-4 w-4" />
            <span>{t("WRICEF distribution")}</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {totalByWricef.length === 0 ? (
              <span className="text-xs text-muted-foreground italic">
                {t("No data")}
              </span>
            ) : (
              totalByWricef.map(([letter, n]) => {
                const safe = letter as WricefLetter;
                return (
                  <Tooltip key={letter}>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className={`text-xs ${WRICEF_COLORS[safe] ?? ""}`}
                      >
                        {safe}: {n}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      {wricefNameOf(t, safe) || safe} —{" "}
                      {tp(t, n, "{{n}} object", "{{n}} objects")}
                    </TooltipContent>
                  </Tooltip>
                );
              })
            )}
          </div>
          {notApplicable > 0 && (
            // Nota, e não badge. O número continua dito: some com ele e a soma
            // dos badges deixa de fechar com o total catalogado.
            <div
              data-testid="wricef-not-applicable"
              className="mt-2 text-[11px] leading-snug text-muted-foreground"
            >
              {t(
                "{{n}} objects have no WRICEF letter by design — classes, function groups and interfaces are part of a deliverable, not deliverables themselves.",
                { n: notApplicable.toLocaleString("en-US") },
              )}
            </div>
          )}
          {topModules.length > 0 && (
            <>
              <Separator className="my-2" />
              <div className="text-xs text-muted-foreground">
                {t("Top modules:")}{" "}
                <span className="text-foreground">
                  {topModules
                    .map(([m, n]) => `${m} (${n})`)
                    .join(" · ")}
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {summary.criticality.avg != null && (
        <Card className="shadow-none border md:col-span-2 xl:col-span-4">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4 text-xs">
              <span className="font-medium text-muted-foreground">
                {t("Criticality distribution ·")}
              </span>
              <span>
                {t("average")}{" "}
                <strong className="text-foreground">
                  {summary.criticality.avg.toFixed(2)}
                </strong>
              </span>
              <span className="text-muted-foreground">
                {t("median")}{" "}
                <strong className="text-foreground">
                  {summary.criticality.p50?.toFixed(2) ?? "—"}
                </strong>
              </span>
              <span className="text-muted-foreground">
                p90{" "}
                <strong className="text-foreground">
                  {summary.criticality.p90?.toFixed(2) ?? "—"}
                </strong>
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===========================================================================
// Filters panel — blocos agrupados
// ===========================================================================

function FiltersPanel({
  selectedLetters,
  onToggleLetter,
  selectedStreams,
  onToggleStream,
  criticality,
  onCriticalityChange,
  sourceFilter,
  onSourceFilterChange,
  objectState,
  onObjectStateChange,
  selectedDecisions,
  onToggleDecision,
  countsByDecision,
  countsByWricef,
  countsByStream,
  streamLabels,
  hasActiveFilters,
  onReset,
}: {
  selectedLetters: Set<WricefLetter>;
  onToggleLetter: (letter: WricefLetter) => void;
  selectedStreams: Set<string>;
  onToggleStream: (stream: string) => void;
  criticality: [number, number];
  onCriticalityChange: (v: [number, number]) => void;
  sourceFilter: string;
  onSourceFilterChange: (v: string) => void;
  objectState: string;
  onObjectStateChange: (v: string) => void;
  selectedDecisions: Set<DecisionOrUndecided>;
  onToggleDecision: (token: DecisionOrUndecided) => void;
  /** A contagem da lista, que acompanha os outros filtros (#1500). */
  countsByDecision?: Record<string, number>;
  countsByWricef?: Partial<Record<WricefBucket, number>>;
  countsByStream?: Record<string, number>;
  streamLabels?: Record<string, string>;
  hasActiveFilters: boolean;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const streams = useMemo(() => streamsBySize(countsByStream), [countsByStream]);

  return (
    <Card className="shadow-none border">
      <CardContent className="pt-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Search className="h-4 w-4 text-muted-foreground" />
            <span>{t("Refine analysis")}</span>
          </div>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="text-xs h-7"
            >
              {t("Clear filters")}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {/* --- Categoria WRICEF --- */}
          <FilterBlock
            label={t("WRICEF category")}
            hint={t("Customization type")}
          >
            <div className="flex flex-wrap gap-1.5">
              {WRICEF_LETTERS.map((l) => {
                const active = selectedLetters.has(l);
                const count = countsByWricef?.[l] ?? 0;
                return (
                  <Tooltip key={l}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => onToggleLetter(l)}
                        className={`px-2.5 py-1 text-xs rounded border transition tabular-nums ${
                          active
                            ? WRICEF_COLORS[l]
                            : count === 0
                            ? "bg-muted/30 text-muted-foreground border-input cursor-not-allowed opacity-50"
                            : "bg-background text-foreground border-input hover:bg-muted"
                        }`}
                        disabled={count === 0 && !active}
                      >
                        {l}{" "}
                        <span className="text-[10px] opacity-70">
                          ({count})
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{wricefNameOf(t, l)}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </FilterBlock>

          {/* --- Módulo SAP --- */}
          {/* O eixo resolvido (#922), e não o `module` do ingest, que a passada
              leve estampa como CROSS em tudo. Os nomes são os da taxonomia APPL
              (TAPLT), e por isso botões e não texto livre. */}
          <FilterBlock
            label={t("SAP module")}
            hint={t("From the application component")}
            className="md:col-span-2"
          >
            {streams.length === 0 ? (
              <span className="text-xs text-muted-foreground italic">{t("No data")}</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {streams.map(([stream, count]) => {
                  const active = selectedStreams.has(stream);
                  return (
                    <button
                      key={stream}
                      type="button"
                      data-testid="module-filter-chip"
                      aria-pressed={active}
                      onClick={() => onToggleStream(stream)}
                      className={`text-[11px] px-1.5 py-0.5 rounded border tabular-nums ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-input hover:bg-muted"
                      }`}
                    >
                      {streamLabel(stream, streamLabels)}{" "}
                      <span className="opacity-70">({count})</span>
                    </button>
                  );
                })}
              </div>
            )}
          </FilterBlock>

          {/* --- Criticidade --- */}
          <FilterBlock
            label={t("Criticality")}
            hint={`${criticality[0].toFixed(2)} – ${criticality[1].toFixed(2)}`}
          >
            <Slider
              min={0}
              max={1}
              step={0.05}
              value={criticality}
              onValueChange={(v) => onCriticalityChange(v as [number, number])}
              className="mt-3"
            />
            <div className="flex gap-1 mt-2">
              <PresetButton
                label={t("High")}
                active={criticality[0] === 0.7 && criticality[1] === 1}
                onClick={() => onCriticalityChange([0.7, 1])}
              />
              <PresetButton
                label={t("Medium")}
                active={criticality[0] === 0.4 && criticality[1] === 0.7}
                onClick={() => onCriticalityChange([0.4, 0.7])}
              />
              <PresetButton
                label={t("Low")}
                active={criticality[0] === 0 && criticality[1] === 0.4}
                onClick={() => onCriticalityChange([0, 0.4])}
              />
            </div>
          </FilterBlock>

          {/* --- O que a lista mostra (#1083) --- */}
          {/* Grupo de função e classe são PEÇA de um entregável, e o consultor
              voltou quatro vezes a isso olhando a tela: "esses FUGR você nem
              considera aqui, o principal são os executáveis". Eles não somem,
              mudam de lugar. */}
          <FilterBlock
            label={t("What the list shows")}
            hint={t("Parts of a deliverable live inside it: open the row to see them")}
          >
            <Select value={objectState} onValueChange={onObjectStateChange}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder={t("What the list shows")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="entregavel">{t("Deliverables")}</SelectItem>
                <SelectItem value="peca">{t("Parts of a deliverable")}</SelectItem>
                {/* As duas listas que garantem que nada some (#1083): a peça
                    que nenhum executável alcança, e o gerado pelo SAP, que
                    não é peça perdida. */}
                <SelectItem value="without_executable">
                  {t("Parts no executable uses")}
                </SelectItem>
                <SelectItem value="generated">{t("Generated by SAP")}</SelectItem>
                <SelectItem value="configuracao">{t("Configuration")}</SelectItem>
                <SelectItem value="all">{t("Everything")}</SelectItem>
              </SelectContent>
            </Select>
          </FilterBlock>

          {/* --- Decisão do arquiteto (#1500) --- */}
          {/* Liga e desliga cada uma. A contagem vem da lista e acompanha os
              outros filtros, então é o que aparece ao ligar o botão. Objeto sem
              letra não tem decisão e não conta. Na segunda linha e largo: os
              rótulos são longos, e numa coluna só o bloco quebrava em quatro
              linhas e esticava a primeira linha do painel inteira. */}
          <FilterBlock
            label={t("Decision")}
            hint={t("Architect's decision")}
            className="md:col-span-2 xl:col-span-4"
          >
            <DecisionChips
              selected={selectedDecisions}
              counts={countsByDecision}
              onToggle={onToggleDecision}
            />
          </FilterBlock>

          {/* --- Origem --- */}
          {/* O status de revisão saiu com a coluna: ele deriva da descrição, que
              a passada leve nunca escreve. O detalhe segue no modal do objeto. */}
          <FilterBlock label={t("Row source")}>
            <Select value={sourceFilter} onValueChange={onSourceFilterChange}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder={t("Data source")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("All sources")}</SelectItem>
                <SelectItem value="zip">{t("SYCM / ATC (ZIP)")}</SelectItem>
                <SelectItem value="tadir">{t("TADIR (RFC)")}</SelectItem>
                <SelectItem value="both">{t("Both sources")}</SelectItem>
              </SelectContent>
            </Select>
          </FilterBlock>
        </div>
      </CardContent>
    </Card>
  );
}

/** O quadrado de cor de uma decisão, o mesmo do card WRICEF do Ato 1 (#1500). */
function DecisionSwatch({
  token,
  size = "md",
}: {
  token: DecisionOrUndecided;
  /** `sm` nos botões compactos do painel. */
  size?: "sm" | "md";
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "shrink-0 rounded-sm",
        size === "sm" ? "h-2 w-2" : "h-2.5 w-2.5",
        DECISION_BAR[token],
      )}
    />
  );
}

/**
 * O filtro por decisão no "Refine analysis" (#1500): liga e desliga cada uma,
 * e fica desabilitado quando não há nenhum objeto daquela decisão com os
 * outros filtros. No tamanho dos botões de módulo, e não no das letras: os
 * rótulos são longos.
 */
function DecisionChips({
  selected,
  counts,
  onToggle,
}: {
  selected: Set<DecisionOrUndecided>;
  counts?: Record<string, number>;
  onToggle: (token: DecisionOrUndecided) => void;
}) {
  const { t } = useTranslation();
  const meta = makeDecisionMeta(t);
  return (
    <div className="flex flex-wrap gap-1">
      {DECISION_ORDER.map((token) => {
        const active = selected.has(token);
        const count = counts?.[token] ?? 0;
        return (
          <button
            key={token}
            type="button"
            data-testid={`panel-decision-filter-${token}`}
            aria-pressed={active}
            onClick={() => onToggle(token)}
            disabled={count === 0 && !active}
            className={cn(
              "inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border transition tabular-nums",
              active
                ? "bg-primary text-primary-foreground border-primary"
                : count === 0
                ? "bg-muted/30 text-muted-foreground border-input cursor-not-allowed opacity-50"
                : "bg-background text-muted-foreground border-input hover:bg-muted",
            )}
          >
            <DecisionSwatch token={token} size="sm" />
            {meta[token].label}{" "}
            <span className="opacity-70">({formatCount(count)})</span>
          </button>
        );
      })}
    </div>
  );
}

/** O valor de "All" no menu do filtro: nenhuma decisão escolhida. */
const ALL_DECISIONS = "_ALL";

/**
 * O filtro por decisão ao lado do CSV (#1500), como no mockup da EF-ARCH-001
 * ("Filtrar decisão: Todas ▾"): um botão que abre as opções, "All" e uma
 * decisão por vez. O número de cada opção é o que aparece ao escolhê-la, e a
 * opção sem nenhum objeto fica desabilitada.
 *
 * O botão diz a escolha. Com várias ligadas pelo painel, ele diz quantas, e
 * nenhuma opção fica marcada. Fica na barra do CSV, fora dos ramos de
 * carregando e vazio da grade: se a decisão escolhida não der linha nenhuma, o
 * filtro continua à vista para trocar a escolha.
 */
function DecisionFilterMenu({
  selected,
  counts,
  onChoose,
}: {
  selected: Set<DecisionOrUndecided>;
  counts?: Record<string, number>;
  onChoose: (token: DecisionOrUndecided | null) => void;
}) {
  const { t } = useTranslation();
  const meta = makeDecisionMeta(t);
  const [only] = selected;
  const value = selected.size === 0 ? ALL_DECISIONS : selected.size === 1 ? only : "";
  const choice =
    selected.size === 0
      ? t("All")
      : selected.size === 1
      ? meta[only].label
      : tp(t, selected.size, "{{n}} decision", "{{n}} decisions");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8" data-testid="catalog-decision-filter">
          {t("Decision filter: {{choice}}", { choice })}
          <ChevronDown className="h-3.5 w-3.5 ml-1" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[240px]">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) =>
            onChoose(next === ALL_DECISIONS ? null : (next as DecisionOrUndecided))
          }
        >
          <DropdownMenuRadioItem
            value={ALL_DECISIONS}
            data-testid="catalog-decision-filter-all"
            className="text-xs"
          >
            {t("All")}
          </DropdownMenuRadioItem>
          {DECISION_ORDER.map((token) => {
            const count = counts?.[token] ?? 0;
            return (
              <DropdownMenuRadioItem
                key={token}
                value={token}
                data-testid={`catalog-decision-filter-${token}`}
                disabled={count === 0 && !selected.has(token)}
                className="text-xs"
              >
                <span className="inline-flex items-center gap-1.5">
                  <DecisionSwatch token={token} />
                  {meta[token].label}
                </span>
                <span className="ml-auto pl-4 tabular-nums text-muted-foreground">
                  {formatCount(count)}
                </span>
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FilterBlock({
  label,
  hint,
  className = "",
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-semibold text-foreground">{label}</span>
        {hint && (
          <span className="text-[10px] text-muted-foreground">{hint}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function PresetButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-[10px] px-1.5 py-0.5 rounded border transition ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-background text-muted-foreground border-input hover:bg-muted"
      }`}
    >
      {label}
    </button>
  );
}

// ===========================================================================
// Catalog content — table com estados
// ===========================================================================

function CatalogContent({
  loading,
  error,
  onRetry,
  items,
  total,
  totalInProject,
  hasActiveFilters,
  orderBy,
  onOrderByChange,
  orderDesc,
  onOrderDescToggle,
  onExport,
  exporting,
  projectId,
  reportQuery,
  decisionCounts,
  selectedDecisions,
  onChooseDecision,
  withoutReason,
  onSelect,
  page,
  onPageChange,
  selection,
  selectionBar,
}: {
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  items: WricefCatalogItem[];
  /** Total do filtro, e não o tamanho da página. */
  total: number;
  decisionCounts?: Record<string, number>;
  selectedDecisions: Set<DecisionOrUndecided>;
  onChooseDecision: (token: DecisionOrUndecided | null) => void;
  /** O filtro do "Fora do escopo" sem motivo (EF-FUNC-011) e quantos são. */
  withoutReason: { count: number; active: boolean; onToggle: () => void };
  /** Total do catálogo (ou do snapshot), sem filtro. */
  totalInProject: number;
  hasActiveFilters: boolean;
  orderBy: WricefCatalogQuery["order_by"];
  onOrderByChange: (v: WricefCatalogQuery["order_by"]) => void;
  orderDesc: boolean;
  onOrderDescToggle: () => void;
  onExport: () => void;
  exporting: boolean;
  projectId: string;
  /** O filtro atual da lista, sem a página: o relatório para o cliente parte dele (#1620). */
  reportQuery: WricefCatalogQuery;
  onSelect: (item: WricefCatalogItem) => void;
  page: number;
  onPageChange: (page: number) => void;
  /** A seleção do lote (#1501); sem ela, a grade fica sem caixas. */
  selection?: CatalogSelection;
  /** A barra do lote, logo acima da grade. */
  selectionBar?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Card className="shadow-none border">
      <CardContent className="pt-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            <span>
              {total === 0
                ? t("WRICEF catalog")
                : tp(t, total, "{{n}} object cataloged", "{{n}} objects cataloged", {
                    n: total.toLocaleString("en-US"),
                  })}
              {hasActiveFilters &&
                totalInProject > total &&
                ` ${t("(of {{n}} in the project)", { n: totalInProject.toLocaleString("en-US") })}`}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={orderBy as string}
              onValueChange={(v) =>
                onOrderByChange(v as WricefCatalogQuery["order_by"])
              }
            >
              <SelectTrigger className="h-8 text-sm w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="criticality_score">
                  {t("Sort by criticality")}
                </SelectItem>
                <SelectItem value="obj_name">{t("Sort by name")}</SelectItem>
                <SelectItem value="wricef">{t("Sort by WRICEF")}</SelectItem>
                <SelectItem value="devclass">{t("Sort by package")}</SelectItem>
                <SelectItem value="stream">{t("Sort by module")}</SelectItem>
                {/* #906: a API já aceitava `created_on` e o `ORDER BY` já é
                    `NULLS LAST` nos dois sentidos, então os sem data caem no
                    fim em vez de encabeçar uma página vazia. Faltava a opção. */}
                <SelectItem value="created_on">{t("Sort by age")}</SelectItem>
                <SelectItem value="updated_at">{t("Last updated")}</SelectItem>
                {/* #1500: na ordem do card do Ato 1, com o objeto sem letra no fim. */}
                <SelectItem value="decision">{t("Sort by decision")}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={onOrderDescToggle}
              title={orderDesc ? t("Descending") : t("Ascending")}
              className="h-8 w-8 p-0"
            >
              {orderDesc ? "↓" : "↑"}
            </Button>
            {/* EF-FUNC-011: antes do filtro de decisão, que fica colado ao CSV. */}
            {(withoutReason.count > 0 || withoutReason.active) && (
              <Button
                variant={withoutReason.active ? "default" : "outline"}
                size="sm"
                className="h-8"
                aria-pressed={withoutReason.active}
                onClick={withoutReason.onToggle}
                title={t("Out-of-scope decisions still missing the reason: select them and give the reason in bulk")}
              >
                {t("Out of scope with no reason")}
                <span className="ml-1.5 tabular-nums">{formatCount(withoutReason.count)}</span>
              </Button>
            )}
            {/* #1500: ao lado do CSV, como no mockup da EF-ARCH-001. */}
            <DecisionFilterMenu
              selected={selectedDecisions}
              counts={decisionCounts}
              onChoose={onChooseDecision}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              disabled={total === 0 || exporting}
              className="h-8"
              title={t("Download every object that matches the filters, not only this page")}
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5 mr-1" />
              )}
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const pid = items[0]?.project_id;
                if (!pid) return;
                void downloadWricefRepository(pid).catch((e) =>
                  console.error("download do repositório WRICEF falhou", e),
                );
              }}
              disabled={items.length === 0}
              className="h-8"
              title={t("Download the entire WRICEF repository (.abap + manifest + README) as a ZIP")}
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              {t("Repository")}
            </Button>
            {/* #1620: o .xlsx que vai ao cliente, a partir do filtro desta lista. */}
            {totalInProject > 0 && (
              <WricefClientReportButton projectId={projectId} query={reportQuery} />
            )}
            {/* #1622: o primeiro corte da avaliação, com a procedência na justificativa. */}
            {totalInProject > 0 && <WricefUsageRuleButton projectId={projectId} />}
          </div>
        </div>

        {loading ? (
          <TableSkeleton />
        ) : error ? (
          <ErrorState error={error} onRetry={onRetry} />
        ) : totalInProject === 0 ? (
          <EmptyState />
        ) : items.length === 0 ? (
          <FilteredEmptyState />
        ) : (
          <>
            {/* #1501: entre as ferramentas e a grade, e não entre o filtro de
                decisão e o CSV, que ficam lado a lado como no mockup. */}
            {selectionBar}
            {/* Em cima e embaixo: embaixo, o botão flutuante do Chat cobre o
                Next quando a tabela termina no rodapé da tela. */}
            <CatalogPager
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={onPageChange}
            />
            <CatalogTable items={items} onSelect={onSelect} selection={selection} />
            <CatalogPager
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={onPageChange}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2 py-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function ErrorState({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{t("Could not load the catalog")}</AlertTitle>
      <AlertDescription className="space-y-2">
        <p className="text-xs">{error.message}</p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> {t("Retry")}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="text-center py-12 space-y-3">
      <Inbox className="h-10 w-10 text-muted-foreground mx-auto" />
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {t("No WRICEF objects cataloged yet")}
        </p>
        <p className="text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
          {t("Run the cataloging pipeline to process the project's SAP sources and generate the inventory. After it runs, this screen will show the classified objects, the computed criticality and functional descriptions for review.")}
        </p>
      </div>
    </div>
  );
}

function FilteredEmptyState() {
  const { t } = useTranslation();
  return (
    <div className="text-center py-10 space-y-2">
      <Search className="h-8 w-8 text-muted-foreground mx-auto" />
      <p className="text-sm font-medium">{t("No results for the filters")}</p>
      <p className="text-xs text-muted-foreground">
        {t("Clear or adjust the filters to see more objects.")}
      </p>
    </div>
  );
}

// ===========================================================================
// Catalog table — leitura humana
// ===========================================================================

function CatalogTable({
  items,
  onSelect,
  selection,
}: {
  items: WricefCatalogItem[];
  onSelect: (item: WricefCatalogItem) => void;
  selection?: CatalogSelection;
}) {
  const { t } = useTranslation();
  const selectable = items.filter((row) => row.wricef).map((row) => row.obj_name);
  const checkedOnPage = selectable.filter((name) => selection?.checked.has(name)).length;
  const pageState =
    checkedOnPage === 0 ? false : checkedOnPage === selectable.length ? true : "indeterminate";
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {selection && (
              <TableHead className="w-[36px]">
                {selectable.length > 0 && (
                  <DecisionCheckbox
                    checked={pageState}
                    onChange={(checked) => selection.onToggle(selectable, checked)}
                    label={t("Select the objects on this page")}
                  />
                )}
              </TableHead>
            )}
            <TableHead>{t("Object")}</TableHead>
            <TableHead className="w-[90px]">
              <ColumnHint
                label={t("WRICEF")}
                legend={WRICEF_LETTERS.map((l) => ({
                  code: l,
                  name: wricefNameOf(t, l),
                }))}
              />
            </TableHead>
            {/* #1500: a decisão do arquiteto e os valores que valem, logo ao lado
                da letra, que é o que define se o objeto tem decisão. Sem legenda
                no cabeçalho: a célula já diz a decisão por extenso. */}
            <TableHead className="w-[170px]">{t("Decision")}</TableHead>
            <TableHead className="w-[130px]">{t("Conversion strategy")}</TableHead>
            <TableHead className="w-[130px]">{t("Estimated hours")}</TableHead>
            <TableHead className="w-[260px]">{t("Module")}</TableHead>
            <TableHead className="w-[180px]">{t("Criticality")}</TableHead>
            <TableHead className="w-[100px]">
              <ColumnHint
                label={t("Clean Core")}
                legend={(["A", "B", "C", "D"] as const).map((lv) => ({
                  code: lv,
                  name: cleanCoreLevelMeta(lv).description,
                }))}
              />
            </TableHead>
            {/* Sem a coluna Author, a pedido (16/10/2026). O autor continua no
                banco, na API, no CSV e no detalhe do objeto (Technical). */}
            <TableHead className="w-[100px]">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 cursor-help border-b border-dotted border-muted-foreground/40">
                    {t("Age")}
                    <Info className="h-3 w-3 opacity-50" aria-hidden="true" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px] text-xs">
                  {t("Age since the object was created in the TADIR. Roughly a third of the objects carry no date there, usually because they were migrated from an older release, and those say so instead of showing an age.")}
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className="w-[110px]">{t("Row source")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((row) => {
            const crit = getCriticalityLabel(row.criticality_score);
            const wricefName = wricefNameOf(t, row.wricef);
            const cc = cleanCoreLevelMeta(row.clean_core_level);
            return (
              <TableRow
                key={row.id}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => onSelect(row)}
              >
                {selection && (
                  // A linha inteira abre o detalhe; o clique na caixa só marca.
                  <TableCell onClick={row.wricef ? (e) => e.stopPropagation() : undefined}>
                    {row.wricef && (
                      <DecisionCheckbox
                        checked={selection.checked.has(row.obj_name)}
                        onChange={(checked) => selection.onToggle([row.obj_name], checked)}
                        label={t("Select {{name}}", { name: row.obj_name })}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <div className="font-mono text-xs font-medium">
                    {row.obj_name}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {row.obj_type_tadir}
                    {row.devclass ? ` · ${row.devclass}` : ""}
                  </div>
                  <SourceReadBadge item={row} />
                  <DemoContentBadge show={row.demo_content === true} />
                </TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className={`text-xs ${WRICEF_COLORS[row.wricef]}`}
                      >
                        {row.wricef}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>{wricefName}</TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <DecisionCell item={row} />
                </TableCell>
                <TableCell>
                  <StrategyCell item={row} />
                </TableCell>
                <TableCell>
                  <HoursCell item={row} />
                </TableCell>
                <TableCell className="text-xs">
                  <ModuleCell item={row} />
                </TableCell>
                <TableCell>
                  <CriticalityCell score={row.criticality_score} label={crit} />
                </TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className={`text-xs ${CLEAN_CORE_BADGE_COLOR[cc.tone]}`}
                      >
                        {cc.label}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {cc.description}
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell className="text-xs">
                  <AgeCell createdOn={row.created_on} />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {getSourceLabel(row.source)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** O traço de "não há", nas células da decisão. */
function NoValue() {
  return <span className="text-xs text-muted-foreground">—</span>;
}

/**
 * A decisão do arquiteto (#1500), com a cor e o rótulo do card WRICEF do Ato 1.
 *
 * Objeto sem letra fica com traço, e não com "No decision": ele não tem
 * decisão nem entra no universo do card (decisão 7 do épico #1343). Em
 * standard, o scope item vem embaixo, como no drill-down do Diagnóstico.
 */
function DecisionCell({ item }: { item: WricefCatalogItem }) {
  const { t } = useTranslation();
  if (!item.wricef) {
    return (
      <div data-testid="decision-cell">
        <NoValue />
      </div>
    );
  }
  const token: DecisionOrUndecided = item.decision ?? UNDECIDED;
  return (
    <div data-testid="decision-cell" className="text-xs">
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          data-testid="decision-swatch"
          className={cn("h-2.5 w-2.5 shrink-0 rounded-sm", DECISION_BAR[token])}
        />
        {makeDecisionMeta(t)[token].label}
      </span>
      {token === "covered_by_standard" && item.decision_standard_scope_item && (
        <div className="pl-4 text-[11px] text-muted-foreground">
          {item.decision_standard_scope_item}
        </div>
      )}
    </div>
  );
}

/** A estratégia que vale: o ajuste do arquiteto, senão a sugestão da IA (#1500). */
function StrategyCell({ item }: { item: WricefCatalogItem }) {
  const { t } = useTranslation();
  const meta = makeExtensionTargetMeta(t) as Record<string, { label: string } | undefined>;
  const strategy = item.decision_strategy;
  return (
    <div data-testid="decision-strategy-cell" className="flex flex-wrap items-center gap-1 text-xs">
      {strategy ? (meta[strategy]?.label ?? strategy) : <NoValue />}
      {strategy && item.decision_strategy_adjusted && <AdjustedBadge />}
    </div>
  );
}

/** As horas que valem (#1500). Fora do escopo e standard dão "0h", de propósito. */
function HoursCell({ item }: { item: WricefCatalogItem }) {
  const label = hoursLabel(item.decision_hours);
  return (
    <div data-testid="decision-hours-cell" className="flex flex-wrap items-center gap-1 text-xs">
      {label ?? <NoValue />}
      {label && item.decision_hours_adjusted && <AdjustedBadge />}
    </div>
  );
}

/**
 * A idade em anos, que é a unidade da pergunta que o Ato 3 faz ("este Z tem
 * doze anos e roda todo dia"), com a data por baixo para o número ser
 * conferível.
 *
 * Sem data **não** vira idade zero: a TADIR não data objeto migrado de release
 * antigo, e isso chega a dois terços do corpus. Zero ali seria objeto novo.
 */
function AgeCell({ createdOn }: { createdOn: string | null }) {
  const { t } = useTranslation();
  const years = yearsSince(createdOn);
  if (years == null) {
    return <span className="text-muted-foreground italic">{t("Not dated")}</span>;
  }
  return (
    <div>
      <div className="font-medium tabular-nums">
        {tp(t, years, "{{n}} year", "{{n}} years")}
      </div>
      <div className="text-[11px] tabular-nums text-muted-foreground">
        {yearOfDateOnly(createdOn)}
      </div>
    </div>
  );
}

function CriticalityCell({
  score,
  label,
}: {
  score: number | null;
  label: ReturnType<typeof getCriticalityLabel>;
}) {
  const { t } = useTranslation();
  if (score == null) {
    return <span className="text-xs text-muted-foreground italic">{t("No score")}</span>;
  }
  const pct = Math.round(score * 100);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={`text-[11px] ${CRITICALITY_BADGE_COLOR[label.tone]}`}
          >
            {label.label}
          </Badge>
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden min-w-[36px]">
            <div
              className={`h-full ${CRITICALITY_BAR_COLOR[label.tone]}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[11px] font-mono tabular-nums w-7 text-right text-muted-foreground">
            {pct}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        {label.description}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * O módulo resolvido (#922). Herdado do pacote é marcado, porque não é o mesmo
 * que medido: o objeto não tem componente de aplicação, e o pacote em que ele
 * mora tem um só entre os objetos que têm.
 */
function ModuleCell({ item }: { item: WricefCatalogItem }) {
  const { t } = useTranslation();
  if (!item.resolved_stream) {
    return (
      <span className="text-muted-foreground italic">
        {streamLabel(item.resolved_stream)}
      </span>
    );
  }
  if (!item.stream_inherited) return <span>{item.resolved_stream}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-help">
          {item.resolved_stream}
          <span
            data-testid="stream-inherited"
            className="rounded border border-dashed border-muted-foreground/50 px-1 text-[10px] text-muted-foreground"
          >
            {t("package")}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        {t(
          "Inherited from package {{pkg}}: the object has no application component of its own, and every object in the package that has one agrees on it.",
          { pkg: item.devclass },
        )}
      </TooltipContent>
    </Tooltip>
  );
}

// ===========================================================================
// Drill panel — análise por objeto
// ===========================================================================

/** O módulo resolvido por extenso, dizendo quando ele veio do pacote. */
function moduleDetail(
  item: WricefCatalogItem,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const label = streamLabel(item.resolved_stream);
  return item.stream_inherited ? t("{{label}} (inherited from package)", { label }) : label;
}

type DetailSectionKey =
  | "resumo"
  | "decisao"
  | "components"
  | "diagnostico"
  | "eols"
  | "criticidade"
  | "tecnico";

function ObjectDetailModal({
  projectId,
  item,
}: {
  projectId: string;
  item: WricefCatalogItem;
}) {
  const { t } = useTranslation();
  const componentsQuery = useWricefComponents(projectId, item.obj_name);
  const componentCount = componentsQuery.data?.length ?? 0;
  const crit = getCriticalityLabel(item.criticality_score);
  const wricefName = wricefNameOf(t, item.wricef);
  const conf = getConfidenceLabel(item.analysis_confidence);
  const rich = hasRichAnalysis(item);
  const eols = item.eol_signals ?? [];

  // O cabeçalho, a linha "Conversion" do Overview e a seção "Decision and
  // adjustment" (#1343, F2b) usam a mesma chave do React Query: abrir a seção
  // não lê de novo, e salvar atualiza os três (`setQueryData` do hook). Sem
  // letra, a consulta fica desligada. A aba Diagnosis não lê a decisão: ela é
  // a análise da IA, e guarda o que foi sugerido.
  const decision = useWricefDecision(projectId, item.wricef ? item.obj_name : undefined).data;
  const extensionMeta = makeExtensionTargetMeta(t) as Record<string, { label: string } | undefined>;
  // A estratégia ajustada quando há uma; senão, a análise de hoje.
  const conversionLabel = decision?.strategy_override
    ? (extensionMeta[decision.strategy_override]?.label ?? decision.strategy_override)
    : item.conversion_tipo
      ? getConversionLabel(item.conversion_tipo)
      : null;
  // E as horas que valem: o efetivo da decisão (0 em fora do escopo e
  // standard) quando há decisão lida; senão, o calculado pela análise.
  const effort = formatEffort(decision ? decision.hours : item.conversion_esforco_h);
  const conversionAdjusted =
    decision?.strategy_override != null || decision?.hours_override != null;

  const sections: {
    key: DetailSectionKey;
    label: string;
    icon: typeof FileText;
    badge?: number;
  }[] = [
    { key: "resumo", label: t("Overview"), icon: ClipboardList },
    // A decisão do arquiteto (#1343, F2), logo depois do Overview, como na Tela
    // B da EF. Só para objeto com letra WRICEF: o tipo diz que toda linha tem
    // letra, mas peça, órfão, biblioteca e configuração chegam sem ela, e a
    // API recusa esses objetos (`not_a_deliverable`).
    ...(item.wricef
      ? [{ key: "decisao" as const, label: t("Decision and adjustment"), icon: Scale }]
      : []),
    // O que compõe o executável (#1083): "grupo de função, classe, chamadas de
    // métodos e tudo mais. No detalhe daquela transação".
    {
      key: "components",
      label: t("Components"),
      icon: Boxes,
      ...(componentCount > 0 ? { badge: componentCount } : {}),
    },
    ...(rich
      ? [{ key: "diagnostico" as const, label: t("Diagnosis"), icon: FileText }]
      : []),
    ...(eols.length
      ? [
          {
            key: "eols" as const,
            label: t("Deprecation"),
            icon: ShieldAlert,
            badge: eols.length,
          },
        ]
      : []),
    { key: "criticidade", label: t("Criticality"), icon: Activity },
    { key: "tecnico", label: t("Technical"), icon: Wrench },
  ];

  const [active, setActive] = useState<DetailSectionKey>("resumo");

  return (
    <>
      {/* ===== Header-resumo (fixo) — o veredito de relance ===== */}
      <div className="border-b px-6 py-4 space-y-2">
        <div className="flex items-center gap-2 flex-wrap pr-8">
          <DialogTitle className="font-mono text-base">
            {item.obj_name}
          </DialogTitle>
          <Badge
            variant="outline"
            className={`text-xs ${WRICEF_COLORS[item.wricef]}`}
          >
            {item.wricef} · {wricefName}
          </Badge>
          {conversionLabel && (
            <Badge
              variant="outline"
              className={`text-xs ${
                CONVERSION_COLORS[item.conversion_tipo ?? ""] ??
                "bg-gray-100 text-gray-800 border-gray-200"
              }`}
            >
              {conversionLabel}
              {effort ? ` · ${effort}` : ""}
            </Badge>
          )}
          {decision?.is_manual_adjustment && (
            <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-200">
              {t("Adjusted")}
            </Badge>
          )}
          <Badge
            variant="outline"
            className={`text-xs ${CRITICALITY_BADGE_COLOR[crit.tone]}`}
          >
            {t("Criticality")} {crit.label}
          </Badge>
          {item.analysis_confidence != null && (
            <Badge
              variant="outline"
              className={`text-xs ${CONFIDENCE_BADGE_COLOR[conf.tone]}`}
            >
              {t("Confidence")} {conf.pct}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {item.obj_type_tadir}
          {item.devclass ? ` · ${t("package")} ${item.devclass}` : ""}
          {` · ${moduleDetail(item, t)}`}
        </p>
        {item.source_read === false && (
          <p
            className={`text-xs ${
              item.type_has_code === false ? "text-muted-foreground" : "text-amber-700"
            }`}
          >
            {sourceReadExplanation(t, item)}
          </p>
        )}
        {item.demo_content && (
          <p className="text-xs text-violet-700">{t(
            "SAP demo content: the author is SAP, and it entered because the connection declared its package as demo. It is not customer development.",
          )}</p>
        )}
      </div>

      {/* ===== Corpo: navegação (esq) + conteúdo (dir) ===== */}
      <div className="flex flex-1 min-h-0">
        <nav className="w-48 shrink-0 border-r bg-muted/30 p-2 space-y-0.5 overflow-y-auto">
          {sections.map((s) => {
            const Icon = s.icon;
            const isActive = active === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setActive(s.key)}
                className={`w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-left transition-colors ${
                  isActive
                    ? "bg-background font-medium shadow-sm"
                    : "text-muted-foreground hover:bg-background/60"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1">{s.label}</span>
                {s.badge != null && (
                  <span className="text-[10px] font-mono bg-amber-100 text-amber-800 rounded-full px-1.5 leading-relaxed">
                    {s.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="flex-1 min-w-0 overflow-y-auto p-6 text-sm">
          {active === "resumo" && (
            <SectionResumo
              item={item}
              conversion={{ label: conversionLabel, effort, adjusted: conversionAdjusted }}
            />
          )}
          {/* Só monta quando é aberta: abrir o detalhe não lê a decisão. */}
          {active === "decisao" && (
            <WricefDecisionSection projectId={projectId} objName={item.obj_name} />
          )}
          {active === "components" && (
            <SectionComponents
              item={item}
              components={componentsQuery.data}
              loading={componentsQuery.isLoading}
              failed={Boolean(componentsQuery.error)}
            />
          )}
          {active === "diagnostico" && <ConversionDiagnostic item={item} />}
          {active === "eols" && <SectionEols item={item} />}
          {active === "criticidade" && <SectionCriticidade item={item} />}
          {active === "tecnico" && <SectionTecnico item={item} />}
        </div>
      </div>
    </>
  );
}

// --- Fonte lido ou não (#1083) --------------------------------------------

/**
 * Os dois casos que o `has_code` do Guia separa, e que pedem leituras opostas.
 * Na base do primeiro cliente são 13 entregáveis sem fonte lida: 10 são
 * leitura que falhou (formulário Adobe e enhancement têm fonte) e 3 são assim
 * por natureza (web service, IDoc, serviço do Gateway). Sem a marca, os dois
 * aparecem com descrição vazia e ninguém sabe qual é defeito.
 *
 * Tipo que o Guia não conhece chega com `type_has_code` verdadeiro, e cai como
 * leitura falhada: presumir o contrário esconderia uma.
 */
function sourceReadExplanation(
  t: ReturnType<typeof useTranslation>["t"],
  item: Pick<WricefCatalogItem, "type_has_code">,
): string {
  return item.type_has_code === false
    ? t("This type has no source code by nature, so there is nothing to read. Expected, not a failure.")
    : t("This type has source code, and reading it failed. Description and parts stay unknown until it is read again.");
}

// --- Conteúdo de demonstração da SAP (#1072) ---------------------------------

/**
 * O IDES é quase todo conteúdo de demonstração da SAP: 38.638 dos 38.639
 * objetos Z e Y têm autor SAP. Eles só entram no catálogo pelos pacotes que a
 * conexão declarou, e um catálogo assim não pode ser lido como desenvolvimento
 * de cliente. O aviso e a marca existem para isso.
 */
function DemoContentBanner({ count }: { count: number }) {
  const { t } = useTranslation();
  if (count <= 0) return null;
  return (
    <div
      data-testid="demo-content-banner"
      className="flex items-center gap-2.5 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm"
    >
      <Info className="h-4 w-4 text-violet-700 shrink-0" />
      <span className="text-violet-900">
        {tp(
          t,
          count,
          "{{n}} object in this catalog is SAP demo content, from a package the connection declared as demo. It is not customer development.",
          "{{n}} objects in this catalog are SAP demo content, from packages the connection declared as demo. They are not customer development.",
        )}
      </span>
    </div>
  );
}

function DemoContentBadge({ show }: { show: boolean }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="mt-1 ml-1 text-[10px] font-normal bg-violet-50 text-violet-800 border-violet-200"
        >
          {t("SAP demo")}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">{t(
            "SAP demo content: the author is SAP, and it entered because the connection declared its package as demo. It is not customer development.",
          )}</TooltipContent>
    </Tooltip>
  );
}

function SourceReadBadge({
  item,
}: {
  item: Pick<WricefCatalogItem, "source_read" | "type_has_code">;
}) {
  const { t } = useTranslation();
  // `undefined` é a api anterior, que não dizia: sem marca, em vez de acusar.
  if (item.source_read !== false) return null;
  const byNature = item.type_has_code === false;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={`mt-1 text-[10px] font-normal ${
            byNature
              ? "bg-gray-50 text-gray-600 border-gray-200"
              : "bg-amber-50 text-amber-800 border-amber-200"
          }`}
        >
          {byNature ? t("No source code") : t("Source not read")}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        {sourceReadExplanation(t, item)}
      </TooltipContent>
    </Tooltip>
  );
}

// --- Seção: Componentes (#1083) --------------------------------------------

/**
 * O que o objeto usa, em árvore, e quem o usa.
 *
 * A árvore vem pronta da api, em ordem de exibição, e a indentação é a
 * profundidade. A biblioteca aparece debaixo de cada executável que a usa, que
 * é o que a pessoa quer ver ao abrir um deles.
 *
 * Lista vazia só vira "não usa peça nenhuma" quando o fonte foi lido. Sem
 * fonte, o vazio é desconhecimento, e afirmar ausência seria o mesmo erro que a
 * marca na linha existe para evitar.
 */
function SectionComponents({
  item,
  components,
  loading,
  failed,
}: {
  item: WricefCatalogItem;
  components: WricefComponent[] | undefined;
  loading: boolean;
  failed: boolean;
}) {
  const { t } = useTranslation();
  const owners = item.unit_owners ?? [];
  const list = components ?? [];

  let usesBody: React.ReactNode;
  if (loading) {
    usesBody = (
      <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t("Loading components…")}
      </p>
    );
  } else if (failed) {
    usesBody = (
      <p className="text-xs text-red-700">{t("Could not load the components.")}</p>
    );
  } else if (list.length > 0) {
    usesBody = (
      <ul className="space-y-1">
        {list.map((c, i) => (
          <li
            key={`${c.via}/${c.obj_name}/${i}`}
            className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs"
            style={{ paddingLeft: `${(c.depth - 1) * 1.25}rem` }}
          >
            <span className="font-mono font-medium">{c.obj_name}</span>
            <span className="text-muted-foreground">{c.obj_type_tadir}</span>
            {c.unit_role === "biblioteca" && (
              <Badge variant="outline" className="text-[10px] font-normal">
                {t("Shared")}
              </Badge>
            )}
            {c.depth > 1 && (
              <span className="text-muted-foreground italic">
                {t("via {{name}}", { name: c.via })}
              </span>
            )}
            <SourceReadBadge item={c} />
            {c.description_pt && (
              <span className="basis-full text-muted-foreground">{c.description_pt}</span>
            )}
          </li>
        ))}
      </ul>
    );
  } else if (item.source_read === false) {
    usesBody = (
      <p className="text-xs text-muted-foreground">
        {item.type_has_code === false
          ? t("This type has no source code, so its parts cannot be read from it.")
          : t("The source of this object was not read, so what it uses is not known.")}
      </p>
    );
  } else {
    usesBody = (
      <p className="text-xs text-muted-foreground">
        {t("No Z part of the catalog is cited in the source of this object.")}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {item.unit_role === "configuracao" && (
        <p className="text-xs text-muted-foreground">
          {t("Generated by SAP: its owner is the transaction that generates it, and on the target it is regenerated, not rewritten.")}
        </p>
      )}
      {owners.length > 0 && (
        <DrillSection title={t("Used by")}>
          <ul className="flex flex-wrap gap-1.5">
            {owners.map((o) => (
              <li key={o}>
                <Badge variant="outline" className="font-mono text-xs font-normal">
                  {o}
                </Badge>
              </li>
            ))}
          </ul>
        </DrillSection>
      )}
      {item.unit_role === "orfao" && (
        <p className="text-xs text-muted-foreground">
          {t("No object in the catalog cites this one.")}
        </p>
      )}
      <DrillSection title={t("Uses")}>
        <p className="text-xs text-muted-foreground">
          {t("What this object uses, read from its source: Z function groups and classes cited by name, and what they use in turn.")}
        </p>
        {usesBody}
      </DrillSection>
    </div>
  );
}

// --- Seção: Resumo (síntese do veredito) ----------------------------------

/** A conversão que vale, calculada uma vez no `ObjectDetailModal` (#1343, F2b). */
interface EffectiveConversion {
  label: string | null;
  effort: string | null;
  adjusted: boolean;
}

function SectionResumo({
  item,
  conversion,
}: {
  item: WricefCatalogItem;
  conversion: EffectiveConversion;
}) {
  const { t } = useTranslation();
  const rs = getReviewStatus(item);
  const crit = getCriticalityLabel(item.criticality_score);
  const conf = getConfidenceLabel(item.analysis_confidence);
  const eols = item.eol_signals ?? [];
  const cc = cleanCoreLevelMeta(item.clean_core_level);

  return (
    <div className="space-y-5">
      <Alert className={`${REVIEW_STATUS_COLORS[rs.tone]} border`}>
        <AlertTitle className="text-xs font-semibold">{rs.label}</AlertTitle>
        <AlertDescription className="text-xs">{rs.description}</AlertDescription>
      </Alert>

      <dl className="grid grid-cols-[11rem_1fr] gap-x-4 gap-y-2">
        <dt className="text-muted-foreground">{t("WRICEF category")}</dt>
        <dd className="font-medium">
          {item.wricef} · {wricefNameOf(t, item.wricef)}
          {item.wricef_fallback ? ` (${t("fallback")})` : ""}
        </dd>

        <dt className="text-muted-foreground">{t("Conversion")}</dt>
        <dd className="flex flex-wrap items-center gap-2 font-medium">
          <span>
            {conversion.label ?? "—"}
            {conversion.effort ? ` · ${conversion.effort}` : ""}
          </span>
          {conversion.adjusted && (
            <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-200">
              {t("Adjusted")}
            </Badge>
          )}
        </dd>

        <dt className="text-muted-foreground">{t("Analysis confidence")}</dt>
        <dd className="font-medium">
          {conf.pct !== "—" ? `${conf.pct} (${conf.label})` : "—"}
        </dd>

        <dt className="text-muted-foreground">{t("Criticality")}</dt>
        <dd className="font-medium">
          {crit.label}
          {item.criticality_score != null &&
            ` · ${(item.criticality_score * 100).toFixed(0)}/100`}
        </dd>

        <dt className="text-muted-foreground">{t("Clean Core")}</dt>
        <dd className="font-medium">
          {cc.level ? `${cc.label} · ${cc.description}` : "—"}
        </dd>

        <dt className="text-muted-foreground">{t("S/4HC scope item")}</dt>
        <dd className="font-medium">{item.scope_item_candidato ?? "—"}</dd>

        <dt className="text-muted-foreground">{t("SAP module")}</dt>
        <dd className="font-medium">{moduleDetail(item, t)}</dd>

        {/* A LoB é a do modelo por objeto, que só a passada profunda roda. Na
            leve o `module` vem vazio (#906), e a linha inteira fica de fora:
            mostrar "—" onde nunca houve resposta é ruído, não informação. */}
        {hasRichAnalysis(item) && (
          <>
            <dt className="text-muted-foreground">{t("LoB / Sub-LoB (AI)")}</dt>
            <dd className="font-medium">
              {item.module ?? "—"}
              {item.sub_lob ? ` / ${item.sub_lob}` : ""}
            </dd>
          </>
        )}

        <dt className="text-muted-foreground">{t("Deprecation signals")}</dt>
        <dd className="font-medium">{eols.length}</dd>
      </dl>

      {item.description_pt && (
        <DrillSection title={t("Functional description")}>
          <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
            {item.description_pt}
          </p>
        </DrillSection>
      )}
    </div>
  );
}

// --- Seção: Sinais de descontinuação (EOLs) -------------------------------

function SectionEols({ item }: { item: WricefCatalogItem }) {
  const { t } = useTranslation();
  const eols = item.eol_signals ?? [];
  const details = item.analysis_details ?? {};
  const tabelas = [
    ...(details.tabelas_standard ?? []),
    ...(details.tabelas_custom ?? []),
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("Techniques/objects deprecated or restricted in S/4HANA Cloud Public detected in this analysis.")}
      </p>
      <ul className="space-y-2">
        {eols.map((eol, i) => (
          <li
            key={`${eol.item}-${i}`}
            className="rounded-md border bg-amber-50/50 border-amber-200 px-3 py-2"
          >
            <p className="text-sm font-medium text-amber-900 flex items-center gap-1.5">
              <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0" />
              {eol.item}
            </p>
            <p className="text-xs text-amber-800/90 leading-snug mt-1">
              {eol.razao}
            </p>
          </li>
        ))}
      </ul>
      {tabelas.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">{t("Referenced tables:")}</span>{" "}
          <span className="font-mono">{tabelas.join(", ")}</span>
        </p>
      )}
    </div>
  );
}

// --- Seção: Criticidade (fatores) -----------------------------------------

function SectionCriticidade({ item }: { item: WricefCatalogItem }) {
  const { t } = useTranslation();
  const crit = getCriticalityLabel(item.criticality_score);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{crit.description}</p>
        <Badge
          variant="outline"
          className={`text-xs shrink-0 ${CRITICALITY_BADGE_COLOR[crit.tone]}`}
        >
          {crit.label}
          {item.criticality_score != null &&
            ` · ${(item.criticality_score * 100).toFixed(0)}/100`}
        </Badge>
      </div>

      {item.criticality_factors ? (
        <div className="space-y-3">
          {CRITICALITY_FACTOR_EXPLANATIONS.map((f) => {
            const v = item.criticality_factors?.[f.key];
            if (v == null) return null;
            return (
              <div key={f.key} className="space-y-1">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-medium flex-1">{f.label}</span>
                  <span className="text-muted-foreground">{t("weight")} {f.weight}</span>
                  <span className="font-mono tabular-nums w-8 text-right">
                    {v.toFixed(2)}
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500"
                    style={{ width: `${v * 100}%` }}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {f.describe(v)}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">
          {t("Factor breakdown not yet computed for this object.")}
        </p>
      )}
    </div>
  );
}

// --- Seção: Técnico (metadados + source) ----------------------------------

function SectionTecnico({ item }: { item: WricefCatalogItem }) {
  const { t } = useTranslation();
  const wricefName = wricefNameOf(t, item.wricef);
  const d = item.analysis_details ?? {};
  const hasDetails = Boolean(
    d.fms_chamados?.length || d.transacoes_alvo?.length || d.observacoes,
  );

  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <MetaRow label={t("Name")} value={item.obj_name} mono />
        <MetaRow label={t("TADIR type")} value={item.obj_type_tadir} />
        <MetaRow
          label={t("WRICEF category")}
          value={`${item.wricef} (${wricefName})`}
        />
        {item.wricef_fallback && (
          <MetaRow label={t("Classification")} value="Automatic fallback" tone="warn" />
        )}
        <MetaRow label={t("Package (devclass)")} value={item.devclass ?? "—"} />
        <MetaRow label={t("SAP module")} value={moduleDetail(item, t)} />
        <MetaRow label={t("Author")} value={item.author ?? "—"} />
        <MetaRow label={t("Created on")} value={formatDateOnly(item.created_on)} />
        <MetaRow label={t("Row source")} value={getSourceLabel(item.source)} />
        <MetaRow
          label={t("Updated on")}
          value={formatDateTime(item.updated_at)}
        />
      </dl>

      {hasDetails && (
        <DrillSection title={t("Analysis details")}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            {d.fms_chamados?.length ? (
              <>
                <dt className="text-muted-foreground">{t("Called FMs")}</dt>
                <dd className="font-mono">{d.fms_chamados.join(", ")}</dd>
              </>
            ) : null}
            {d.transacoes_alvo?.length ? (
              <>
                <dt className="text-muted-foreground">{t("Transactions")}</dt>
                <dd className="font-mono">{d.transacoes_alvo.join(", ")}</dd>
              </>
            ) : null}
          </dl>
          {d.observacoes ? (
            <p className="text-xs text-muted-foreground mt-2">{d.observacoes}</p>
          ) : null}
        </DrillSection>
      )}

      {item.code_storage_path && (
        <DrillSection title={t("ABAP source")}>
          <p className="text-[11px] font-mono text-muted-foreground break-all mb-2">
            {item.code_storage_path}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => {
                void openWricefSourceInTab(
                  item.project_id,
                  item.obj_name,
                ).catch((e) =>
                  console.error(
                    `abrir ${item.obj_name}.abap em nova aba falhou`,
                    e,
                  ),
                );
              }}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t("Open in new tab")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => {
                void downloadWricefSource(
                  item.project_id,
                  item.obj_name,
                ).catch((e) =>
                  console.error(`download de ${item.obj_name}.abap falhou`, e),
                );
              }}
            >
              <Download className="h-3.5 w-3.5" />
              {t("Download .abap")}
            </Button>
          </div>
        </DrillSection>
      )}
    </div>
  );
}

function ConversionDiagnostic({ item }: { item: WricefCatalogItem }) {
  const { t } = useTranslation();
  const conf = getConfidenceLabel(item.analysis_confidence);
  const effort = formatEffort(item.conversion_esforco_h);

  return (
    <DrillSection
      title={t("Conversion diagnosis")}
      right={
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className={`text-xs ${CONFIDENCE_BADGE_COLOR[conf.tone]}`}
            >
              {t("Confidence")} {conf.pct}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs">
            {t(
              "Confidence of the contextual analysis ({{label}}). The higher it is, the more evidence the agent found in the code + knowledge base.",
              { label: conf.label },
            )}
          </TooltipContent>
        </Tooltip>
      }
    >
      {/* Alternativa de conversão */}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        {item.conversion_tipo && (
          <Badge
            variant="outline"
            className={`text-xs ${
              CONVERSION_COLORS[item.conversion_tipo] ??
              "bg-gray-100 text-gray-800 border-gray-200"
            }`}
          >
            {getConversionLabel(item.conversion_tipo)}
          </Badge>
        )}
        {effort && (
          <span className="text-xs text-muted-foreground">
            {t("Estimated effort:")}{" "}
            <span className="font-medium">{effort}</span>
          </span>
        )}
      </div>
      {item.conversion_descricao && (
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          {item.conversion_descricao}
        </p>
      )}

      {/* Scope item candidato + sub-LoB */}
      {(item.scope_item_candidato || item.sub_lob) && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs mb-3">
          {item.scope_item_candidato && (
            <>
              <dt className="text-muted-foreground">{t("S/4HC scope item")}</dt>
              <dd className="font-medium">{item.scope_item_candidato}</dd>
            </>
          )}
          {item.sub_lob && (
            <>
              <dt className="text-muted-foreground">{t("Sub-LoB")}</dt>
              <dd className="font-medium">{item.sub_lob}</dd>
            </>
          )}
        </dl>
      )}

      {/* Evidência da classificação WRICEF */}
      {item.wricef_evidence && (
        <div className="mb-3">
          <p className="text-[11px] font-medium text-muted-foreground mb-0.5">
            {t("Classification evidence")}
          </p>
          <p className="text-xs leading-relaxed">{item.wricef_evidence}</p>
        </div>
      )}

    </DrillSection>
  );
}

function DrillSection({
  title,
  right,
  children,
}: {
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">{title}</h4>
        {right}
      </div>
      {children}
    </section>
  );
}

function MetaRow({
  label,
  value,
  mono,
  tone,
  fullWidth,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "warn";
  fullWidth?: boolean;
}) {
  return (
    <>
      <dt
        className={`text-muted-foreground ${fullWidth ? "col-span-2" : ""}`}
      >
        {label}
      </dt>
      <dd
        className={`${mono ? "font-mono" : ""} ${
          tone === "warn" ? "text-amber-700 font-medium" : ""
        } ${fullWidth ? "col-span-2 break-all" : ""}`}
      >
        {value}
      </dd>
    </>
  );
}
